import { accountModuleStore } from '@app/reducers/account-module/account-module.state';
import { type ContractTypeModel } from '@app/reducers/orm/contract-type/contract-type.model';
import { getContractTypeEntities } from '@app/reducers/orm/contract-type/contract-type.service';
import { type ContractModel } from '@app/reducers/orm/contract/contract.model';
import { contractPeriodFilter, getContracts } from '@app/reducers/orm/contract/contract.service';
import { type EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import {
  employeeHasTeamInDepartments,
  getEmployees,
  getEmployeeTeamDepartments,
  type EmployeeTeamDepartments,
} from '@app/reducers/orm/employee/employee.selectors';
import { createSelector } from '@ngrx/store';
import { type ObjectMap } from '@reducers/orm/orm.interfaces';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';

import { type PayrollRunScope } from './payroll.model';

export interface BalanceStepEmployeesInput {
  employees: Pick<EmployeeModel, 'id'>[];
  contracts: ContractModel[];
  contractTypes: ObjectMap<ContractTypeModel>;
  minijobEnabled: boolean;
  employeeTeamDepartments: EmployeeTeamDepartments;
  scope: PayrollRunScope;
}

/**
 * Employees the balance step lists: those holding a contract at any point in the payroll period,
 * within the run's departments, and holding at least one contract with a plus/min balance this
 * step can correct.
 *
 * The period is a range, not a date — `contractPeriodFilter` is the same overlap test the employee
 * list uses, which calls it with the same date twice. An employee whose contract ended mid-period
 * still belongs here: their balance is exactly the kind payroll exists to settle, so they are
 * listed even though the correction screen will show them as not correctable at the period's end
 * date.
 */
export function employeeIdsForBalanceStep({
  employees,
  contracts,
  contractTypes,
  minijobEnabled,
  employeeTeamDepartments,
  scope,
}: BalanceStepEmployeesInput): string[] {
  const startDate = toISODateString(scope.range.start);
  const endDate = toISODateString(scope.range.end);

  const employeeIdsWithCorrectableContract = new Set(
    contracts
      .filter(contractPeriodFilter(startDate, endDate))
      .filter((contract) => hasCorrectableBalance(contractTypes[contract.contract_type_id], minijobEnabled))
      .map((contract) => contract.user_id),
  );

  return employees
    .filter((employee) => {
      const employeeId = employee.id ?? '';

      if (!employeeIdsWithCorrectableContract.has(employeeId)) {
        return false;
      }

      return employeeHasTeamInDepartments(employeeId, employeeTeamDepartments, scope.departmentIds);
    })
    .map((employee) => employee.id ?? '');
}

/**
 * A Minijob contract's excess hours go to the Minijob balance, which is settled outside this step
 * (a dedicated Minijob step follows in the same Story, PR #5126), so such a contract leaves nothing
 * for this step to correct.
 *
 * Two escapes from that, both real: the account may not have the Minijob module at all, and a
 * contract type can be given plus/min after it was created a Minijob type. A type carrying both
 * builds a plus/min balance like any other, so only `minijob` without `plus_min` is left out.
 */
function hasCorrectableBalance(contractType: ContractTypeModel | undefined, minijobEnabled: boolean): boolean {
  if (!minijobEnabled) {
    return true;
  }

  return !contractType?.minijob || contractType.plus_min;
}

export const getEmployeeIdsForBalanceStep = (scope: PayrollRunScope) =>
  createSelector(
    getEmployees,
    getContracts,
    getContractTypeEntities,
    accountModuleStore.selectMinijobEnabled,
    getEmployeeTeamDepartments,
    (employees, contracts, contractTypes, minijobEnabled, employeeTeamDepartments) =>
      employeeIdsForBalanceStep({
        employees,
        contracts,
        contractTypes,
        minijobEnabled,
        employeeTeamDepartments,
        scope,
      }),
  );

/**
 * Balances the manager still has to settle. The backend answers only for employees whose contract
 * carries plus/min at all, so an employee missing from the response has nothing to balance rather
 * than a balance of zero.
 *
 * Rounded to five decimals to match the correction screen's own arithmetic, so a balance the
 * screen renders as zero is not counted as outstanding work.
 */
export function countOutOfBalance(balances: Record<string, number>): number {
  return Object.values(balances).filter((balance) => parseFloat(balance.toFixed(5)) !== 0).length;
}
